import React, { Fragment, type ReactElement } from 'react'

import type { RSheetRecordNode, RSheetsNodeBlockRow, UnionOfRSheetColumn } from '../../../domain/column'
import { type BlockHeaderStyle, RSheetsNodeBlockHeader } from './RSheetsNodeBlockHeader'

/**
 * RSheet のヘッダー行。カラムを順番に並べる素直な実装ですが、列固定とドラッグ&ドロップ
 * (列移動・列幅調整) のロジックが入っているのでやや見通しが悪く見えます。
 *
 * ヘッダーの上端には `RSheetsNodeBlockHeader` (青や緑や黄色のバー) が表示され、
 * 「どのテーブルに属するカラム群か」を視覚的に伝えます。SALESCORE のシートは複数テーブルを
 * 1 シートで扱えるため、テーブル境界の視覚化が必要になります。
 */
const RSheetsHeaderRow = <T extends UnionOfRSheetColumn<RSheetRecordNode>>({
  targetColumns,
  headerTopOffset,
  columnIndexStart,
  maxDepth,
  nodeBlockHeader,
  width,
  renderHeaderCell,
  blockHeaderStyle,
}: {
  targetColumns: T[]
  headerTopOffset: number
  columnIndexStart: number
  maxDepth: number
  nodeBlockHeader: RSheetsNodeBlockRow[]
  width: number
  renderHeaderCell: (column: T, columnIndex: number) => ReactElement
  blockHeaderStyle: BlockHeaderStyle
}): ReactElement => (
  <>
    <div style={{ height: 'inherit', overflow: 'hidden', width: 'inherit' }} className="absolute">
      <RSheetsNodeBlockHeader
        nodeBlockHeader={nodeBlockHeader}
        maxDepth={maxDepth}
        blockHeaderStyle={blockHeaderStyle}
      />
    </div>
    <div
      style={{
        width,
        top: headerTopOffset,
      }}
      className="absolute flex"
    >
      {targetColumns.map((column, columnIndex) => (
        <Fragment key={columnIndex}>{renderHeaderCell(column, columnIndex + columnIndexStart)}</Fragment>
      ))}
    </div>
  </>
)

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- React.memoを通すと型推論が壊れるので仕方がない。多分効果が低い使い方なので廃止も視野に入れる。
export const RSheetsHeaderRowMemo = React.memo(RSheetsHeaderRow) as unknown as <
  T extends UnionOfRSheetColumn<RSheetRecordNode>,
>(
  properties: React.ComponentProps<typeof RSheetsHeaderRow<T>>,
) => ReactElement
